import React, { useEffect, useState } from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import { PhoneIncoming as PhoneCallbackIcon } from '@phosphor-icons/react';
import type { InboxItem } from '../../types/inbox';
import { getCallLogId } from './inboxItemUtils';
import api from '../../lib/api';
import { useRecordingAudio } from '../../hooks/useRecordingAudio';

interface CallLog {
  id: number;
  from_number: string;
  to_number: string;
  direction: 'inbound' | 'outbound';
  duration?: number | null;
  result?: string;
  result_display?: string;
  recording_url?: string | null;
  transcription?: string | null;
  started_at?: string | null;
}

interface InlineCallViewProps {
  item: InboxItem;
  onCall?: () => void;
}

/** Seconds to m:ss. */
const formatDuration = (seconds?: number | null): string | null => {
  if (seconds == null || Number.isNaN(seconds)) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Values come from CallLog.result on the backend — 'answered', not
// 'completed', and 'no-answer' with a hyphen.
const getResultColor = (result?: string) => {
  switch (result) {
    case 'answered':
      return 'success';
    case 'voicemail':
      return 'warning';
    case 'no-answer':
    case 'busy':
      return 'neutral';
    case 'missed':
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
};

const InlineCallView: React.FC<InlineCallViewProps> = ({ item, onCall }) => {
  const [call, setCall] = useState<CallLog | null>(null);
  const [loading, setLoading] = useState(true);
  const callLogId = getCallLogId(item);
  const audio = useRecordingAudio(call?.id, Boolean(call?.recording_url));

  useEffect(() => {
    let cancelled = false;
    if (callLogId == null) {
      setLoading(false);
      return;
    }
    api.get<CallLog>(`/api/inbox/call/${callLogId}/`)
      .then((response) => {
        if (!cancelled) setCall(response.data);
      })
      .catch(() => {
        // Show the "details unavailable" state rather than invented values
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [callLogId]);

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ p: 3 }}>
        <CircularProgress size="sm" />
      </Stack>
    );
  }

  if (!call) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          Call details are unavailable.
        </Typography>
      </Box>
    );
  }

  const duration = formatDuration(call.duration);
  const counterparty = call.direction === 'inbound' ? call.from_number : call.to_number;

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack spacing={1}>
            <Typography level="body-sm" fontWeight="md">
              {call.direction === 'inbound' ? 'Call from' : 'Call to'}:{' '}
              {item.lead_name || counterparty}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {duration && (
                <Typography level="body-sm">Duration: {duration}</Typography>
              )}
              {call.result && (
                <Chip size="sm" color={getResultColor(call.result)} variant="soft">
                  {(call.result_display || call.result).toUpperCase()}
                </Chip>
              )}
            </Stack>
          </Stack>
          {onCall && (
            <Button
              variant="soft"
              size="sm"
              startDecorator={<PhoneCallbackIcon />}
              onClick={onCall}
            >
              Call Back
            </Button>
          )}
        </Stack>

        {/* Recording — only when one exists */}
        {call.recording_url && (
          <Box sx={{ bgcolor: 'background.level1', borderRadius: 'md', p: 2 }}>
            <Typography level="body-sm" fontWeight="md" sx={{ mb: 1 }}>
              Recording
            </Typography>
            {audio.src ? (
              <Box component="audio" controls src={audio.src} sx={{ width: '100%' }} />
            ) : audio.loading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size="sm" />
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Loading recording…
                </Typography>
              </Stack>
            ) : (
              <Typography level="body-sm" sx={{ color: 'danger.400' }}>
                {audio.error || 'Recording unavailable'}
              </Typography>
            )}
          </Box>
        )}

        {/* Transcription — only when one exists */}
        {call.transcription && (
          <Box>
            <Typography level="body-sm" fontWeight="md" sx={{ mb: 1 }}>
              Transcription:
            </Typography>
            <Box
              sx={{
                bgcolor: 'background.level1',
                borderRadius: 'sm',
                p: 2,
                maxHeight: 150,
                overflowY: 'auto',
              }}
            >
              <Typography level="body-sm" sx={{ fontStyle: 'italic' }}>
                {call.transcription}
              </Typography>
            </Box>
          </Box>
        )}

        {!call.recording_url && !call.transcription && (
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            No recording or transcription for this call.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default InlineCallView;
